import React from 'react';

import { Trans } from '@mint-lib/i18n';
import {
  Card,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Suggestions } from '@mint-sdk/api-recommendations';

import { StaticCurrencySpan } from '@myn/mui';

import NotReadBadge from '../../atoms/NotReadBadge/NotReadBadge.jsx';
import SeparateStatusChip from '../../atoms/SeparateStatusChip/SeparateStatusChip.jsx';
import SuggestionBudgetSpan from '../../atoms/SuggestionBudgetSpan/SuggestionBudgetSpan.jsx';
import SuggestionDateSpan from '../../atoms/SuggestionDateSpan/SuggestionDateSpan.jsx';
import SuggestionCardStatus from './SuggestionCardStatus.jsx';

type SuggestionCardProps = {
  isSelected: boolean;
  onSelect: (suggestion: Suggestions.SuggestionResponseType) => void;
  suggestion: Suggestions.SuggestionResponseType;
};

const SuggestionCard = ({
  suggestion,
  onSelect,
  isSelected,
}: SuggestionCardProps) => {
  const isNotRead =
    suggestion.status === Suggestions.Statuses.ACTIVE && suggestion.notRead;

  return (
    <Card
      data-testid="listitem"
      variant="outlined"
      className="w-full cursor-pointer"
      selected={isSelected}
      onClick={() => onSelect(suggestion)}
    >
      <div className="mb-3 flex max-h-5 flex-row justify-between gap-1">
        <div className="flex min-w-0 flex-row gap-1">
          <NotReadBadge invisible={!isNotRead} />

          <TruncateText tooltipClassName="max-w-95 break-all">
            <p
              className={cn(
                'min-w-0 text-center',
                typographyVariants({ variant: 'h4' }),
              )}
            >
              {suggestion.journeyName}
            </p>
          </TruncateText>
        </div>

        <SuggestionCardStatus suggestion={suggestion} />
      </div>

      <div className="flex flex-row justify-between">
        <Trans
          ns="@mint-lib/recommendations-circuit"
          i18nKey="<WrapperIfNeeded>Budget: <Currency/> </WrapperIfNeeded>"
          components={{
            WrapperIfNeeded: <SuggestionBudgetSpan />,
            Currency: (
              <StaticCurrencySpan
                number={suggestion.taxonomy.budgetData.currentBudget ?? 0}
                currency={suggestion.currency}
              />
            ),
          }}
        />

        <SuggestionDateSpan suggestion={suggestion} />
      </div>

      {suggestion.originalId && (
        <Card
          variant="outlined"
          className="mt-2 flex h-9 w-full flex-row items-center"
          contentClassName="flex flex-row w-full p-2 justify-between"
        >
          <TruncateText tooltipClassName="max-w-95 break-all">
            <p
              className={cn(
                'min-w-0 text-center',
                typographyVariants({ variant: 'h4' }),
              )}
            >
              {suggestion.journeyName}
            </p>
          </TruncateText>
          <SeparateStatusChip type="Original" />
        </Card>
      )}
    </Card>
  );
};

export default SuggestionCard;
